<template>
  <h2 class="p-3">Cost-effectiveness of CKD screening</h2>
  <div class="legenddiv" ref="legenddiv"></div>
  <div class="chartdiv" ref="chartdiv"></div>
  <div class="p-3">
    <p><small>ICER, incremental cost-effectiveness ratio; QALY, quality-adjusted life years; WTP, willingness to pay</small></p>
    <p><small>Tip: hover the cursor over the graph to show data labels.</small></p>
  </div>
</template>

<script>
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";

export default {
  name: "CostEffectivenessOfCkdScreening",
  props: {
    country: String,
  },

  async mounted() {
    am5.addLicense("AM5C317022592");
    // Create root element
    let root = am5.Root.new(this.$refs.chartdiv);
    var legendRoot = am5.Root.new(this.$refs.legenddiv);


    // Set themes
    root.setThemes([
      am5themes_Animated.new(root)
    ]);

    // Create chart
    var chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        paddingLeft: 0,
        layout: root.verticalLayout
      })
    );

    // Mouse cursor visual
    // chart.set("cursor", am5xy.XYCursor.new(root, {}));

    // Data
    let response = await fetch(`/data/cost-effectiveness-of-ckd-screening/${this.country}.json`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    const json = await response.json();

    let data = [];

    json.forEach((element) => {
      data.push({
        year: `${element.year}`,
        age45: element.age45,
        age65: element.age65,
        threshold: element.threshold
      });
    });

    let millionCountries = ['colombia', 'hungary', 'india', 'japan', 'south-korea', 'taiwan'].includes(this.country);

    if (millionCountries) {
      // Set number format to 2 decimal places for countries with numbers in the millions
      root.numberFormatter.setAll({
        numberFormat: "#.##",
        numericFields: ["valueY"]
      });
    } else {
      root.numberFormatter.setAll({
        numberFormat: "####.",
        numericFields: ["valueY"]
      });
    }

    // x-axis

    var xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 2021,
        max: 2041,
        numberFormat: "###",
        strictMinMax: true,
        renderer: am5xy.AxisRendererX.new(root, {
          strokeOpacity: 1,
          minGridDistance: 75
        }),
      })
    );

    var xRenderer = xAxis.get("renderer");
    xRenderer.grid.template.setAll({
      stroke: am5.color(0x000000),
      visible: false
    });

    xAxis.data.setAll(data);

    xAxis.children.push(
      am5.Label.new(root, {
        x: am5.percent(50),
        centerX: am5.percent(50),
        y: am5.p50,
        centerY: am5.percent(50),
        paddingTop: 25,
        text: "Year"
      })
    );

    // y-axis renderer
    var yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        numberFormat: "#",
        renderer: am5xy.AxisRendererY.new(root, {
          strokeOpacity: 1,
          minGridDistance: 20
        })
      })
    );

    // hide the grid template on y-axis
    var yRenderer = yAxis.get("renderer");
    yRenderer.grid.template.setAll({
      stroke: am5.color(0x000000),
      visible: false
    });

    // y-axis figure label
    let yAxisLabel = "";

    // additional text to add to y-axis label based on country
    let countryLabels = {
      'australia' : 'ICER (AUS/QALY)',
      'belgium': 'ICER (EUR/QALY)',
      'brazil': 'ICER (BRL/QALY)',
      'canada': 'ICER (CAD/QALY)',
      'china': 'ICER (CNY/QALY)',
      'colombia': 'ICER (COP/QALY), millions',
      'denmark': 'ICER (DKK/QALY)',
      'france': 'ICER (EUR/QALY)',
      'germany': 'ICER (EUR/QALY)',
      'greece': 'ICER (EUR/QALY)',
      'hungary': 'ICER (HUF/QALY), millions',
      'india': 'ICER (INR/QALY), millions',
      'israel': 'ICER (ILS/QALY)',
      'italy': 'ICER (EUR/QALY)',
      'japan': 'ICER (JPY/QALY), millions',
      'mexico': 'ICER (MXN/QALY)',
      'netherlands': 'ICER (EUR/QALY)',
      'philippines': 'ICER (PHP/QALY)',
      'poland': 'ICER (PLN/QALY)',
      'romania': 'ICER (RON/QALY)',
      'saudi-arabia': 'ICER (SAR/QALY)',
      'singapore': 'ICER (SGD/QALY)',
      'south-korea': 'ICER (KRW/QALY), millions',
      'spain': 'ICER (EUR/QALY)',
      'sweden': 'ICER (EUR/QALY)',
      'taiwan': 'ICER (NTD/QALY), millions',
      'thailand': 'ICER (THB/QALY)',
      'turkey': 'ICER (TRY/QALY)',
      'uae': 'ICER (AED/QALY)',
      'uk': 'ICER (GBP/QALY)',
      'us': 'ICER (USD/QALY)'
    };

    // Changes y-Axis label based on country name
    if (countryLabels[this.country]) {
      yAxisLabel += countryLabels[this.country];
    }

    let tooltipCurrency = {
      'australia' : '$',
      'belgium': '€',
      'brazil': '$',
      'canada': '$',
      'china': '¥',
      'colombia': '$',
      'denmark': 'kr',
      'france': '€',
      'germany': '€',
      'greece': '€',
      'hungary': 'Ft',
      'india': '₹',
      'israel': '₪',
      'italy': '€',
      'japan': '¥',
      'mexico': '$',
      'netherlands': '€',
      'philippines': '₱',
      'poland': 'zł',
      'romania': 'lei',
      'saudi-arabia':'ر.س',
      'singapore': 'S$',
      'south-korea': '₩',
      'spain': '€',
      'sweden': '€',
      'taiwan': '$',
      'thailand': '฿',
      'turkey': '₺',
      'uae': 'د.إ',
      'uk': '£',
      'us': '$'
    }

    if (tooltipCurrency[this.country]) {
      tooltipCurrency = tooltipCurrency[this.country];
    }

      yAxis.children.unshift(
        am5.Label.new(root, {
          rotation: 270,
          text: yAxisLabel,
          fontSize: 14,
          fontWeight: 'bold',
          x: am5.p50,
          centerX: am5.percent(50),
          y: am5.p50,
          centerY: am5.percent(50),
          paddingBottom: 40
        })
      );

    // Chart ticks
    // y-axis
    var yRendererTicks = yAxis.get("renderer");
    yRendererTicks.ticks.template.setAll({
      stroke: am5.color(0x000000),
      visible: true
    });

    // x-axis
    var xRendererTicks = xAxis.get("renderer");
    xRendererTicks.ticks.template.setAll({
      stroke: am5.color(0x000000),
      visible: true
    });

    // Add legend
    var legend = legendRoot.container.children.unshift(
      am5.Legend.new(legendRoot, {
        nameField: "name",
        fillField: "color",
        strokeField: "color",
        centerX: am5.percent(50),
        x: am5.percent(56)
      }),
    );

    legend.markerRectangles.template.setAll({
      cornerRadiusTL: 10,
      cornerRadiusTR: 10,
      cornerRadiusBL: 10,
      cornerRadiusBR: 10
    });

    legend.markers.template.setAll({
      width: 14,
      height: 14
    });

    legend.data.setAll([
      {
        name: "ICER (≥ 45 years)",
        color: am5.color("#143762")
      },
      {
        name: "ICER (≥ 65 years)",
        color: am5.color("#e6ad3a")
      },
      {
        name: "WTP threshold",
        color: am5.color("#FF0000")
      },

    ]);

    // Add series
    function makeSeries(name, fieldName, colour) {
      var series = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: name,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: fieldName,
          valueXField: "year",
          stroke: am5.color(colour),
          fill: am5.color(colour),
          tooltip: am5.Tooltip.new(root, {
            labelText: `{name}: ${tooltipCurrency}{valueY}`,
            getFillFromSprite: true,
            autoTextColor: true
          }),
        })
      );

      if (fieldName === 'threshold') {
        series.strokes.template.setAll({
          strokeWidth: 3,
          strokeDasharray: 12
        });

      } else {

        series.strokes.template.setAll({
          strokeWidth: 4,
        });

        series.bullets.push(function() {
          return am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, {
              strokeWidth: 3,
              radius: 4,
              stroke: colour,
              fill: colour
            })
          });
        });
      }

      series.data.setAll(data);
      series.appear();
    }

    chart.set("cursor", am5xy.XYCursor.new(root, {}));

    makeSeries("ICER (≥45 years)", "age45", "#143762");
    makeSeries("ICER (≥65 years)", "age65", "#e6ad3a");
    makeSeries("WTP threshold", "threshold", "#FF0000");

// Make stuff animate on load
    chart.appear(1000, 100);
  }
}

</script>

<style scoped>
.chartdiv {
  width: 100%;
  height: 500px;
}
.legenddiv {
  width: 100%;
  height: 75px;
}
</style>
